/**
 * Phase 83.3 — Auto-generated Resource Cards for the web report.
 *
 * Scans a set of rich-text HTML strings for `<a>` anchors that look like
 * documents (PDF / Google Drive / Notion / PFAP resources) and renders
 * them as clickable cards, mirroring what the PDF renderer does with
 * `resource_card_grid`. Keeps Trial + Season web reports visually
 * aligned with their printable output.
 *
 * Zero DOM parsing dependencies — uses the browser's native DOMParser
 * so we never ship a heavy HTML parser to the frontend bundle.
 */
import React, { useMemo } from 'react';
import { FileText, ExternalLink } from 'lucide-react';

const DOC_EXTENSIONS = ['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx'];
const DOC_HOSTS = ['docs.google.com', 'drive.google.com', 'notion.site', 'notion.so', 'pfap', 'academyresources'];

function looksLikeDoc(href = '') {
  const clean = href.toLowerCase().split('?')[0];
  if (DOC_EXTENSIONS.some(ext => clean.endsWith(ext))) return true;
  try {
    const host = new URL(href).host.toLowerCase();
    return DOC_HOSTS.some(h => host.includes(h));
  } catch { return false; }
}

function extractResources(htmlStrings = []) {
  const parser = new DOMParser();
  const seen = new Set();
  const out = [];
  htmlStrings.forEach(html => {
    if (!html) return;
    const doc = parser.parseFromString(String(html), 'text/html');
    doc.querySelectorAll('a[href]').forEach(a => {
      const href = a.getAttribute('href') || '';
      if (!looksLikeDoc(href)) return;
      const label = (a.textContent || href).trim();
      const key = `${href}|${label}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ href, label });
    });
  });
  return out;
}

export default function ReportResourceCards({ htmlSources = [] }) {
  const resources = useMemo(() => extractResources(htmlSources), [htmlSources]);
  if (resources.length === 0) return null;
  return (
    <section
      className="rounded-lg border border-emerald-100 dark:border-emerald-900/40 bg-emerald-50/40 dark:bg-emerald-900/10 p-5"
      data-testid="report-resource-cards"
    >
      <h3 className="text-sm font-bold uppercase tracking-wide text-emerald-800 dark:text-emerald-200 mb-3">
        Supporting Resources
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {resources.map((r, idx) => (
          <a
            key={`${r.href}-${idx}`}
            href={r.href}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-start gap-3 rounded-md border border-emerald-100 dark:border-emerald-900/40 bg-white dark:bg-gray-900 p-3 hover:border-emerald-400 hover:shadow-sm transition"
            data-testid={`resource-card-${idx}`}
          >
            <div className="mt-0.5 rounded-md bg-emerald-100 dark:bg-emerald-900/40 p-2 text-emerald-700 dark:text-emerald-200">
              <FileText size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                {r.label}
              </p>
              <p className="text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-1 mt-0.5 group-hover:underline">
                Open Resource <ExternalLink size={11} />
              </p>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
